<template>
    <div class="container-fluid form-container fire-risk-form-container">
        <div class="row">
            <div class="col-sm-12">
                <h1 class="page-title">
                    Fire Risk Assessment
                </h1>
            </div>
        </div>
        <form
            action=""
            class="fire-alarm-test-form"
            @submit.prevent="saveForm()"
        >
            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-sm-12">
                            <h3>Customer Details</h3>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-sm-12 col-md-6 form-group">
                            <label for="customer">Customer</label>
                            <customer-search
                                ref="customerSearch"
                                :customer="form.customer"
                                :customer-id="form.customer_id"
                                @change="customerChanged"
                            />
                            <error
                                :errors="errors"
                                name="customer_id"
                            />
                        </div>
                        <div
                            v-if="form.customer && form.customer.sites"
                            class="col-sm-12 col-md-6 form-group"
                        >
                            <label for="site">Site:</label>
                            <select
                                v-if="form.customer.sites.length"
                                id="site"
                                v-model="form.site_id"
                                class="form-control"
                                name="site"
                                placeholder="Site"
                                required
                                @change="siteChanged()"
                            >
                                <option
                                    v-for="site in form.customer.sites"
                                    :key="site.id"
                                    :value="site.id"
                                >
                                    #{{ site.site_no }} {{ site.name }}
                                </option>
                            </select>
                            <span
                                v-if="!form.customer.sites.length"
                                class="form-error"
                            >This customer has no sites</span>
                            <error
                                :errors="errors"
                                name="site_id"
                            />
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-sm-12 col-md-6 form-group">
                            <label for="job_id">Job No.</label>
                            <select
                                id="job_id"
                                v-model="form.job_id"
                                :disabled="!jobs"
                                class="form-control"
                                name="job_id"
                                placeholder="Select a job"
                            >
                                <option
                                    :value="null"
                                    disabled
                                >
                                    Select a job
                                </option>
                                <option
                                    v-for="job in jobs"
                                    :key="job.id"
                                    :value="job.id"
                                >
                                    {{ job.id }}: {{ job.name }}
                                </option>
                            </select>
                            <error
                                :errors="errors"
                                name="job_id"
                            />
                        </div>

                        <div class="col-sm-12 col-md-6 form-group">
                            <label for="doc_no">Document No.</label>
                            <input
                                id="doc_no"
                                v-model="form.doc_no"
                                class="form-control"
                                name="doc_no"
                                placeholder="Document Number"
                                type="text"
                            >
                            <error
                                :errors="errors"
                                name="doc_no"
                            />
                        </div>

                        <div class="col-sm-12 col-md-6 form-group">
                            <label for="service_report_no">Invoice Number</label>
                            <input
                                id="service_report_no"
                                v-model="form.service_report_no"
                                class="form-control"
                                name="service_report_no"
                                placeholder="Service Report Number"
                                type="text"
                            >
                            <error
                                :errors="errors"
                                name="service_report_no"
                            />
                        </div>

                        <div class="col-sm-12 col-md-6 form-group">
                            <label for="po_number">Purchase Order No.</label>
                            <input
                                id="po_number"
                                v-model="form.po_number"
                                class="form-control"
                                name="po_number"
                                placeholder="Purchase Order No."
                                type="text"
                            >
                            <error
                                :errors="errors"
                                name="po_number"
                            />
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-sm-12 col-md-3">
                            <label
                                class="label"
                                for="service_time"
                            >How long did this service take?</label>
                        </div>
                        <div class="col-sm-12 col-md-2">
                            <duration-select-component
                                v-model="form.ra_service_time"
                                required
                            />
                        </div>
                    </div>
                </div>
            </div>

            <job-info
                v-if="form.job_id"
                :job-id="form.job_id"
            />

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h3>Fire Risk Assessment</h3>
                        </div>
                    </div>
                    <date-row
                        id="assessment_date"
                        v-model="form.assessment_date"
                        :options="{format: 'L'}"
                        label="Date of risk assessment"
                    />
                    <select-row
                        id="assessment_type"
                        v-model="form.assessment_type"
                        :options="['New Full Risk Assessment', 'Risk Assessment Review', 'Amendment']"
                        label="Type of Assessment carrying out"
                    />
                    <select-row
                        id="reviewed_12_months"
                        v-model="form.reviewed_12_months"
                        label="Is there a fire risk assessment on site and has it been reviewed in the last 12 months?"
                    />
                    <date-row
                        id="last_assessment_date"
                        v-model="form.last_assessment_date"
                        :options="{format: 'L'}"
                        label="Date of last risk assessment"
                    />
                    <select-row
                        id="last_by_organisation"
                        v-model="form.last_by_organisation"
                        wide
                        :label="`Was the last risk assessment carried out by ${companyName}?`"
                    />
                    <select-row
                        id="action_points_resolved"
                        v-model="form.action_points_resolved"
                        :options="['Yes', 'No', 'N/A']"
                        label="Have all relevant action points been resolved?"
                    />
                    <text-row
                        id="assessor_names"
                        v-model="form.assessor_names"
                        wide
                        label="Assessor name(s)"
                    />
                    <text-row
                        id="validated_by"
                        v-model="form.validated_by"
                        wide
                        label="Report validated by"
                    />
                    <text-row
                        id="responsible_person"
                        wide
                        v-model="form.responsible_person"
                        label="Employer / Responsible person"
                    />
                    <text-row
                        id="persons_consulted"
                        wide
                        v-model="form.persons_consulted"
                        label="Person(s) consulted"
                    />
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h3>1.00 General</h3>
                        </div>
                    </div>
                    <text-row
                        id="floors_above_ground"
                        v-model="form.floors_above_ground"
                        label="1.01 No of floors ground and above"
                        type="textarea"
                        wide
                    />
                    <text-row
                        id="floors_below_ground"
                        v-model="form.floors_below_ground"
                        label="No of floors entirely below ground level"
                        type="textarea"
                        wide
                    />
                    <text-row
                        id="car_parking_floors"
                        v-model="form.car_parking_floors"
                        label="Floors on which car parking is provided:"
                        wide
                    />
                    <text-row
                        id="floor_area"
                        v-model="form.floor_area"
                        label="1.02 Approximate floor area"
                        type="textarea"
                        wide
                    />
                    <text-row
                        id="construction_and_details"
                        v-model="form.construction_and_details"
                        :rows="5"
                        label="1.03 Details of construction and layout"
                        type="textarea"
                        wide
                    />
                    <text-row
                        id="occupancy_type"
                        v-model="form.occupancy_type"
                        label="1.04 Type of occupancy"
                        type="textarea"
                        wide
                    />
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h3>2.00 The Occupants</h3>
                        </div>
                    </div>
                    <text-row
                        id="max_persons"
                        v-model="form.max_persons"
                        label="2.01 Approximate maximum number of persons:"
                    />
                    <text-row
                        id="max_employers_one_time"
                        v-model="form.max_employers_one_time"
                        label="2.02 Approximate maximum number of employees at any one time:"
                    />
                    <text-row
                        id="max_young_persons_one_time"
                        v-model="form.max_young_persons_one_time"
                        label="2.03 Approximate maximum number of young persons at any one time:"
                    />
                    <text-row
                        id="max_public_one_time"
                        v-model="form.max_public_one_time"
                        label="2.04 Approximate maximum number of members of public at any one time:"
                    />
                    <text-row
                        id="hours_of_occupation"
                        v-model="form.hours_of_occupation"
                        label="2.05 Associated times/hours of occupation"
                        wide
                    />
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h3>3.00 Occupants especially at risk from fire</h3>
                        </div>
                    </div>
                    <text-row
                        id="max_persons"
                        v-model="form.sleeping_occupants"
                        label="3.01 Number of sleeping occupants:"
                    />
                    <text-row
                        id="max_employers_one_time"
                        v-model="form.disabled_occupants"
                        label="3.02 Number of disabled occupants:"
                    />
                    <text-row
                        id="max_young_persons_one_time"
                        v-model="form.other_disabled_occupants"
                        label="3.03 Other disabled occupants:"
                    />
                    <text-row
                        id="max_public_one_time"
                        v-model="form.remote_occupants"
                        label="3.04 Occupants in remote areas or lone workers:"
                        wide
                    />
                    <text-row
                        id="hours_of_occupation"
                        v-model="form.young_person_occupants"
                        label="3.05 Number of young persons:"
                    />
                    <text-row
                        id="hours_of_occupation"
                        v-model="form.other_occupants"
                        label="3.06 Others:"
                        wide
                    />
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <text-row
                        id="fire_loss_experience"
                        v-model="form.fire_loss_experience"
                        label="4.00 Fire loss experience:"
                    />
                    <text-row
                        id="other_info"
                        v-model="form.other_info"
                        label="5.00 Other relevant information:"
                    />
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h3>6.00 Relevant fire legislation</h3>
                        </div>
                    </div>
                    <text-row
                        id="fire_legislation"
                        v-model="form.fire_legislation"
                        label="6.01 The following fire legislation applies:"
                        wide
                    />
                    <text-row
                        id="legislation_enforced"
                        v-model="form.legislation_enforced"
                        label="6.02 The above legislation is enforced by:"
                        wide
                    />
                    <text-row
                        id="other_legislation"
                        v-model="form.other_legislation"
                        label="6.03 Other legislation that makes significant requirements for fire precautions in these premises (other than Builder Regulations 2010 as amended):"
                        wide
                    />
                    <text-row
                        id="other_legislation_enforced"
                        v-model="form.other_legislation_enforced"
                        label="6.04 The legislation to which 6.03 refers is enforced:"
                        wide
                    />
                    <text-row
                        id="alterations_in_force"
                        v-model="form.alterations_in_force"
                        label="6.05 Is there alterations in force:"
                        wide
                    />
                    <text-row
                        id="legislation_relevant_info"
                        v-model="form.legislation_relevant_info"
                        label="Relevant information (including description of arrangements and deficiencies observed):"
                    />
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h5>7.00 Electrical sources of ignition</h5>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <table class="table">
                                <tbody>
                                    <risk-row
                                        id="prevent_fires"
                                        :form="form"
                                        label="Reasonable measures taken to prevent fires of electrical origin?"
                                        number="7.01"
                                    />
                                    <risk-row
                                        id="fixed_install_tested"
                                        :form="form"
                                        label="Fixed installation periodically inspected and tested?"
                                        number="7.02"
                                    />
                                    <risk-row
                                        id="portable_appliance_test"
                                        :form="form"
                                        label="Portable appliance testing (where appropriate) carried out?"
                                        number="7.03"
                                    />
                                    <risk-row
                                        id="personal_equip_policy"
                                        :form="form"
                                        label="Suitable policy regarding the use of personal electrical appliances?"
                                        number="7.04"
                                    />
                                    <risk-row
                                        id="trailing_leads_limit"
                                        :form="form"
                                        label="Suitable limitation of trailing leads and adapters?"
                                        number="7.05"
                                    />
                                </tbody>
                            </table>
                            <text-row
                                id="electrcal_relevant_info"
                                wide
                                v-model="form.electrcal_relevant_info"
                                label="Relevant information (including description of arrangements and deficiencies observed):"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h5>8.00 Smoking</h5>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <table class="table">
                                <tbody>
                                    <risk-row
                                        id="smoking_fires_prevent"
                                        :form="form"
                                        label="Reasonable measures taken to prevent fires because of smoking?"
                                        number="8.01"
                                    />
                                    <risk-row
                                        id="smoking_prohibited_primises"
                                        :form="form"
                                        label="Is smoking prohibited on the premises?"
                                        number="8.02"
                                    />
                                    <risk-row
                                        id="smoking_prohibited_areas"
                                        :form="form"
                                        label="Is smoking prohibited in appropriate areas?"
                                        number="8.03"
                                    />
                                    <risk-row
                                        id="smoking_arrangements"
                                        :form="form"
                                        label="Are suitable arrangements in place for those who wish to smoke?"
                                        number="8.04"
                                    />
                                    <risk-row
                                        id="no_smoking_observed"
                                        :form="form"
                                        label="Does the no smoking policy appear to be observed at the time of inspection?"
                                        number="8.05"
                                    />
                                </tbody>
                            </table>
                            <text-row
                                id="smoking_relevant_info"
                                wide
                                v-model="form.smoking_relevant_info"
                                label="Relevant information (including description of arrangements and deficiencies observed):"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h5>9.00 Arson</h5>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <table class="table">
                                <tbody>
                                    <risk-row
                                        id="arson_security"
                                        :form="form"
                                        label="Does basic security against arson by outsiders appear reasonable?"
                                        number="9.01"
                                    />
                                    <risk-row
                                        id="fire_load"
                                        :form="form"
                                        label="Is there an absence of unnecessary fire load in close proximity to the premises or available for ignition by outsiders?"
                                        number="9.02"
                                    />
                                </tbody>
                            </table>
                            <text-row
                                id="smoking_relevant_info"
                                wide
                                v-model="form.smoking_relevant_info"
                                label="Relevant information (including description of arrangements and deficiencies observed):"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h5>10.00 Portable heaters and heating installations</h5>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <table class="table">
                                <tbody>
                                    <risk-row
                                        id="portable_heaters_avoidable"
                                        :form="form"
                                        label="Is the use of portable heaters avoided as far as practicable?"
                                        number="10.01"
                                    />
                                    <risk-row
                                        id="harzardous_appliances_avoided"
                                        :form="form"
                                        label="Is the use of the more hazardous type (e.g. radiant bar fires or LPG) appliances avoided?"
                                        number="10.02"
                                    />
                                    <risk-row
                                        id="minimise_combustible_materials"
                                        :form="form"
                                        label="Are suitable measures taken to minimize the hazard of ignition of combustible materials?"
                                        number="10.03"
                                    />
                                    <risk-row
                                        id="fixed_heating_maintenance"
                                        :form="form"
                                        label="Are fixed heating installations subject to regular maintenance?"
                                        number="10.04"
                                    />
                                </tbody>
                            </table>
                            <text-row
                                id="heating_relevant_info"
                                wide
                                v-model="form.heating_relevant_info"
                                label="Relevant information (including description of arrangements and deficiencies observed):"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h5>11.00 Cooking</h5>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-12">
                            <table class="table">
                                <tbody>
                                    <risk-row
                                        id="prevent_cooking_fires"
                                        :form="form"
                                        label="Are reasonable measures taken to prevent fires because of cooking?"
                                        number="11.01"
                                    />
                                    <risk-row
                                        id="filters_ductwork_cleaned"
                                        :form="form"
                                        label="Filters changed and ductwork cleaned regularly?"
                                        number="11.02"
                                    />
                                    <risk-row
                                        id="extinguishing_available"
                                        :form="form"
                                        label="Suitable extinguishing appliances available?"
                                        number="11.03"
                                    />
                                </tbody>
                            </table>
                            <text-row
                                id="cooking_relevant_info"
                                wide
                                v-model="form.cooking_relevant_info"
                                label="Relevant information (including description of arrangements and deficiencies observed):"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h5>12.00 Lighting</h5>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <table class="table">
                                <tbody>
                                    <risk-row
                                        id="lighting_protection"
                                        :form="form"
                                        label="Do the premises have a lightning protection system?"
                                        number="12.01"
                                    />
                                </tbody>
                            </table>
                            <text-row
                                id="lighting_relevant_info"
                                wide
                                v-model="form.lighting_relevant_info"
                                label="Relevant information (including description of arrangements and deficiencies observed):"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h5>13.00 Other significant ignition sources</h5>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <table class="table">
                                <tbody>
                                    <risk-row
                                        id="avoid_other_ignition_sources"
                                        :form="form"
                                        label="Is there avoidance of other significant ignition sources?"
                                        number="13.01"
                                    />

                                    <risk-row
                                        id="avoid_other_ignition_sources"
                                        :form="form"
                                        label="Is there avoidance of other significant ignition sources?"
                                        number="13.01"
                                    />

                                    <risk-row
                                        id="avoid_frictional_heat"
                                        :form="form"
                                        label="Is there avoidance of activity involving processes such as frictional heat?"
                                        number="13.01"
                                    />

                                    <risk-row
                                        id="avoid_heat_near_materials"
                                        :form="form"
                                        label="Is there avoidance of light bulbs or heating appliances near flammable/combustible materials?"
                                        number="13.02"
                                    />
                                </tbody>
                            </table>
                            <text-row
                                id="ignition_source_relevant_info"
                                wide
                                v-model="form.ignition_source_relevant_info"
                                label="Relevant information (including description of arrangements and deficiencies observed):"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h5>14.00 Housekeeping</h5>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <table class="table">
                                <tbody>
                                    <risk-row
                                        id="housekeeping_standard"
                                        :form="form"
                                        label="Is the standard of housekeeping adequate?"
                                        number="14.01"
                                    />
                                    <risk-row
                                        id="materials_seperated_from_ignition"
                                        :form="form"
                                        label="Are combustible materials separated from ignition sources?"
                                        number="14.02"
                                    />
                                    <risk-row
                                        id="accumulation_combustable_materials"
                                        :form="form"
                                        label="Is there an avoidance of unnecessary accumulation of combustible materials or waste?"
                                        number="14.03"
                                    />
                                    <risk-row
                                        id="storage_combustable_materials"
                                        :form="form"
                                        label="Is there appropriate storage of combustible materials?"
                                        number="14.04"
                                    />
                                    <risk-row
                                        id="inappropriate_storage_combustable_materials"
                                        :form="form"
                                        label="Is there avoidance of inappropriate storage of combustibles materials?"
                                        number="14.05"
                                    />
                                    <risk-row
                                        id="textiles_fire_retardant"
                                        :form="form"
                                        label="Are all textiles and furniture treated to be fire retardant?"
                                        number="14.06"
                                    />
                                    <risk-row
                                        id="walls_ceilings_lined"
                                        :form="form"
                                        label="Are substantial areas of walls or ceilings covered with flammable linings or materials avoided?"
                                        number="14.07"
                                    />
                                </tbody>
                            </table>
                            <text-row
                                id="housekeeping_source_relevant_info"
                                wide
                                v-model="form.housekeeping_source_relevant_info"
                                label="Relevant information (including description of arrangements and deficiencies observed):"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h5>15.00 Hazards introduced by outside contractor and building work</h5>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <table class="table">
                                <tbody>
                                    <risk-row
                                        id="fire_safety_for_contractors"
                                        :form="form"
                                        label="Are fire safety conditions imposed on outside contractors?"
                                        number="15.01"
                                    />
                                    <risk-row
                                        id="control_over_work_by_contractors"
                                        :form="form"
                                        label="Is there satisfactory control over works carried out in the building by outside contractors (including hot work permits)?"
                                        number="15.02"
                                    />
                                    <risk-row
                                        id="in_house_maintenance_personnel"
                                        :form="form"
                                        label="If there are in-house maintenance personnel, are suitable precautions taken during hot work, including use of hot work permits?"
                                        number="15.03"
                                    />
                                </tbody>
                            </table>
                            <text-row
                                id="contractor_hazards_relevant_info"
                                wide
                                v-model="form.contractor_hazards_relevant_info"
                                label="Relevant information (including description of arrangements and deficiencies observed):"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h5>16.00 Dangerous substances</h5>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <table class="table">
                                <tbody>
                                    <risk-row
                                        id="precations_for_hazardous_substances"
                                        :form="form"
                                        label="Are the general fire precautions adequate to address the hazards associated with dangerous substances used or stored within the premises?"
                                        number="16.01"
                                    />
                                    <risk-row
                                        id="hazardous_substance_safety_info"
                                        :form="form"
                                        label="Is there adequate information on safety provided by the supplier on any relevant safety data sheet?"
                                        number="16.02"
                                    />
                                    <tr>
                                        <td class="bold">
                                            16.03
                                        </td>
                                        <td
                                            class="bold"
                                            colspan="3"
                                        >
                                            Has account been taken regarding
                                        </td>
                                    </tr>
                                    <risk-row
                                        id="special_technica_organisational_mesaures"
                                        :form="form"
                                        label="The special, technical and organisational measures and the substances used and their possible interactions."
                                        number="16.03.01"
                                    />
                                    <risk-row
                                        id="amount_of_hazardous_substance"
                                        :form="form"
                                        label="The amount of the substance involved"
                                        number="16.03.02"
                                    />
                                    <risk-row
                                        id="work_with_dangerous_substance"
                                        :form="form"
                                        label="Where the work will involve more than one dangerous substance, the risk presented by such substances in combination"
                                        number="16.03.03"
                                    />
                                    <risk-row
                                        id="hazardous_substance_arrangements"
                                        :form="form"
                                        label="The arrangements for the safe handling, storage and transport of dangerous substances and of waste containing dangerous substances"
                                        number="16.03.04"
                                    />
                                    <risk-row
                                        id="hazardous_substance_mesaures_reg_reform"
                                        :form="form"
                                        label="Any other measures introduced as a result of the Regulatory Reform (Fire Safety) Order 2005 "
                                        number="16.03.05"
                                    />
                                    <risk-row
                                        id="safety_systems_of_work"
                                        :form="form"
                                        label="Are there safe systems of work for maintenance, where there is the potential for a high level of risk?"
                                        number="16.04"
                                    />
                                    <risk-row
                                        id="control_measures_explosive_atmosphere"
                                        :form="form"
                                        label="Are sufficient control measures in place to counter the likelihood that an explosive atmosphere will occur and its persistence including adjacent areas?"
                                        number="16.05"
                                    />
                                    <risk-row
                                        id="control_measures_ignition_sources"
                                        :form="form"
                                        label="Are sufficient control measures in place to counter the likelihood that ignition sources, including electrostatic discharges, will be present and become active and effective"
                                        number="16.06"
                                    />
                                    <risk-row
                                        id="account_taken_scale_effects"
                                        :form="form"
                                        label="Has account been taken regarding the scale of the anticipated effects?"
                                        number="16.07"
                                    />
                                    <risk-row
                                        id="sufficient_safety_info"
                                        :form="form"
                                        label="Is sufficient additional safety information available to enable the assessment to be completed?"
                                        number="16.08"
                                    />
                                    <tr>
                                        <td class="bold">
                                            16.09
                                        </td>
                                        <td
                                            class="bold"
                                            colspan="3"
                                        >
                                            Has account been taken regarding sources of oxygen?
                                        </td>
                                    </tr>
                                    <risk-row
                                        id="no_piped_oxygen"
                                        :form="form"
                                        label="Is there no piped oxygen systems in use?"
                                        number="16.09.01"
                                    />
                                    <risk-row
                                        id="avoidance_oxygen_cylinders"
                                        :form="form"
                                        label="Are there avoidance of oxygen cylinders used or stored?"
                                        number="16.09.02"
                                    />
                                    <risk-row
                                        id="procedure_of_isolation"
                                        :form="form"
                                        label="Is there an operational procedure for isolation?"
                                        number="16.09.03"
                                    />
                                    <risk-row
                                        id="oxidising_materials_safely"
                                        :form="form"
                                        label="Are oxidising materials safely used or stored?"
                                        number="16.09.04"
                                    />
                                    <risk-row
                                        id="absence_other_hazards"
                                        :form="form"
                                        label="Is there an absence of other significant hazards that warrant consideration including process hazards that impact on general fire precautions:"
                                        number="16.10"
                                    />
                                </tbody>
                            </table>
                            <text-row
                                id="dangerous_substrances_relevant_info"
                                wide
                                v-model="form.dangerous_substrances_relevant_info"
                                label="Relevant information (including description of arrangements and deficiencies observed):"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h5>17.00 Fire Protection Measures</h5>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <table class="table">
                                <tbody>
                                    <risk-row
                                        id="reasonable_means_of_escape"
                                        :form="form"
                                        label="It is considered that the premises are provided with reasonable means of escape in case of fire?"
                                        number="17.01"
                                    />
                                    <risk-row
                                        id="adequate_staircases_exit_capacities"
                                        :form="form"
                                        label="Do staircases and exit capacities appear to be adequate for the number of occupants4)?"
                                        number="17.02"
                                    />
                                    <risk-row
                                        id="adequate_design_escape_routes"
                                        :form="form"
                                        label="Is there adequate design of escape routes?"
                                        number="17.03"
                                    />
                                    <risk-row
                                        id="adequate_escape_exit_provisions"
                                        :form="form"
                                        label="Is there adequate provision of escape exits?"
                                        number="17.04"
                                    />
                                    <risk-row
                                        id="exits_immediately_openable"
                                        :form="form"
                                        label="Are exits easily and immediately openable where necessary?"
                                        number="17.05"
                                    />
                                    <risk-row
                                        id="exits_open_in_direction"
                                        :form="form"
                                        label="Do fire exits open in direction of escape where necessary?"
                                        number="17.06"
                                    />
                                    <risk-row
                                        id="slide_revolving_doors_avoided"
                                        :form="form"
                                        label="Are sliding or revolving doors avoided as fire exits where necessary?"
                                        number="17.07"
                                    />
                                    <risk-row
                                        id="securing_exists"
                                        :form="form"
                                        label="Are there satisfactory means for securing exits?"
                                        number="17.08"
                                    />
                                    <risk-row
                                        id="single_direction_travel"
                                        :form="form"
                                        label="Are single direction travel distances reasonable in the circumstances of the case?"
                                        number="17.09"
                                    />
                                    <risk-row
                                        id="alternative_escape_reasonable"
                                        :form="form"
                                        label="Are alternative means of escape travel distances reasonable in the circumstances of the case?"
                                        number="17.10"
                                    />
                                    <risk-row
                                        id="fire_resist_construction_sound_condition"
                                        :form="form"
                                        label="Is the fire-resisting construction protecting escape routes in sound condition?"
                                        number="17.11"
                                    />
                                    <risk-row
                                        id="precautions_inner_inner_rooms"
                                        :form="form"
                                        label="Are suitable fire precautions provided for inner and inner-inner rooms?"
                                        number="17.12"
                                    />
                                    <risk-row
                                        id="escape_routes_obstucted"
                                        :form="form"
                                        label="Are escape routes unobstructed?"
                                        number="17.13"
                                    />
                                    <risk-row
                                        id="reasonable_escape_disabled_people"
                                        :form="form"
                                        label="Is the premises provided with reasonable arrangements for means of escape for disabled people?"
                                        number="17.14"
                                    />
                                    <risk-row
                                        id="exits_right_place_direct"
                                        :form="form"
                                        label="Are the exits in the right place and do the escape routes lead as directly as possible to a place of total safety?"
                                        number="17.15"
                                    />
                                    <risk-row
                                        id="escape_route_maintenance_awareness"
                                        :form="form"
                                        label="Are the people who work in the building aware of the importance of maintaining the safety of the escape routes by ensuring that fire doors are not wedged or held open?"
                                        number="17.16"
                                    />
                                    <risk-row
                                        id="fire_doors_damage_free"
                                        :form="form"
                                        label="Are fire doors free from holes or damage?"
                                        number="17.17"
                                    />
                                    <risk-row
                                        id="fire_doors_signed"
                                        :form="form"
                                        label="Are fire doors provided with appropriate fire doors signs?"
                                        number="17.18"
                                    />
                                    <risk-row
                                        id="external_escape_routes_unobstructed"
                                        :form="form"
                                        label="Are all external escape routes unobstructed and providing a clear route to the fire assembly points?"
                                        number="17.19"
                                    />
                                    <risk-row
                                        id="gap_hole_avoidance"
                                        :form="form"
                                        label="Is there avoidance of holes or gaps in walls, ceilings and floors properly sealed, e.g. where services such as ventilation ducts and electrical cables pass through them?"
                                        number="17.20"
                                    />
                                    <risk-row
                                        id="occupants_escape_reasonable_time"
                                        :form="form"
                                        label="Can all the occupants escape to a place of total safety in a reasonable time?"
                                        number="17.21"
                                    />
                                    <risk-row
                                        id="safe_fire_assembly_points"
                                        :form="form"
                                        label="Are fire assembly points in safe locations?"
                                        number="17.22"
                                    />
                                    <risk-row
                                        id="fire_door_equipment_maintained"
                                        :form="form"
                                        label="Is fire door integrity/strips/seals and all door furniture adequately maintained?"
                                        number="17.23"
                                    />
                                    <risk-row
                                        id="adequate_fire_action_notices"
                                        :form="form"
                                        label="Are fire action notices adequate?"
                                        number="17.24"
                                    />
                                    <risk-row
                                        id="high_risk_rooms_protected"
                                        :form="form"
                                        label="Are high risk rooms adequately protected?"
                                        number="17.25"
                                    />
                                    <risk-row
                                        id="suitable_stairways"
                                        :form="form"
                                        label="If vertical evacuation is necessary (internal or external), are stairways suitable in size and width?"
                                        number="17.26"
                                    />
                                </tbody>
                            </table>
                            <text-row
                                id="fire_protection_relevant_info"
                                wide
                                v-model="form.fire_protection_relevant_info"
                                label="Relevant information (including description of arrangements and deficiencies observed):"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h5>18.00 Fire Development and Spread Limitation</h5>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <table class="table">
                                <tbody>
                                    <risk-row
                                        id="compartmentation_standard"
                                        :form="form"
                                        label="Is it considered that there is compartmentation of a reasonable standard?"
                                        number="18.01"
                                    />
                                    <risk-row
                                        id="linings_limitation"
                                        :form="form"
                                        label="Is it considered that there is reasonable limitation of linings that may promote fire spread?"
                                        number="18.02"
                                    />
                                    <risk-row
                                        id="external_combustible_materials_avoided"
                                        :form="form"
                                        label="Is there avoidance of any external combustible materials such as cladding or balconies?"
                                        number="18.03"
                                    />
                                    <risk-row
                                        id="sprinklers_provided"
                                        :form="form"
                                        label="Are sprinklers if provided of the correct type for the risks present?"
                                        number="18.04"
                                    />
                                    <risk-row
                                        id="fire_damper"
                                        :form="form"
                                        label="As far as can reasonably be ascertained, are fire dampers provided as necessary to protect critical means of escape against passage of fire, smoke and products of combustion in the early stages of a fire?"
                                        number="18.05"
                                    />
                                </tbody>
                            </table>
                            <text-row
                                id="fire_spread_relevant_info"
                                wide
                                v-model="form.fire_spread_relevant_info"
                                label="Relevant information (including description of arrangements and deficiencies observed):"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h5>19.00 Escape Lighting</h5>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <table class="table">
                                <tbody>
                                    <risk-row
                                        id="escape_routes_lighting"
                                        :form="form"
                                        label="Are all the escape routes covered by an acceptable form of emergency lighting?"
                                        number="19.01"
                                    />
                                    <risk-row
                                        id="external_routes_escape_lighting"
                                        :form="form"
                                        label="Are toilets, plants rooms, external fire escape staircases, underground car parks and external escape routes covered by an acceptable form of emergency lighting?"
                                        number="19.02"
                                    />
                                    <risk-row
                                        id="lighting_backup_power"
                                        :form="form"
                                        label="Is there back-up power supplies for the emergency lighting?"
                                        number="19.03"
                                    />
                                </tbody>
                            </table>
                            <text-row
                                id="escape_lighting_relevant_info"
                                wide
                                v-model="form.escape_lighting_relevant_info"
                                label="Relevant information (including description of arrangements and deficiencies observed):"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h5>20.00 Fire Safety Signs and Notices</h5>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <table class="table">
                                <tbody>
                                    <risk-row
                                        id="indicated_by_signs"
                                        :form="form"
                                        label="Where necessary are escape routes and exits, the locations of firefighting equipment and emergency telephones indicated by appropriate signs?"
                                        number="20.01"
                                    />
                                    <risk-row
                                        id="notices_on_security_doors"
                                        :form="form"
                                        label="Where necessary are notices provided such as those giving information on how to operate security devices on exit doors, those indicating doors enclosing fire hazards that must be kept shut and fire action notices for staff and other people?"
                                        number="20.02"
                                    />
                                    <risk-row
                                        id="signs_maintenance"
                                        :form="form"
                                        label="Are you maintaining all the necessary signs and notices so that they continue to be correct, legible and understood?"
                                        number="20.03"
                                    />
                                    <risk-row
                                        id="fire_rescue_signs"
                                        :form="form"
                                        label="Are you maintaining signs that you have provided for the information of the fire and rescue service, such as those indicating the location of water suppression stop valves and the storage of hazardous substances?"
                                        number="20.04"
                                    />
                                </tbody>
                            </table>
                            <text-row
                                id="fire_safety_signs_relevant_info"
                                wide
                                v-model="form.fire_safety_signs_relevant_info"
                                label="Relevant information (including description of arrangements and deficiencies observed):"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h5>21.00 Means of Giving Warning In Case of Fire</h5>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <table class="table">
                                <tbody>
                                    <risk-row
                                        id="manually_operated_alarm_system"
                                        :form="form"
                                        label="Is a reasonable manually operated electrical fire alarm system provided?"
                                        number="21.01"
                                    />
                                    <risk-row
                                        id="alarm_call_points_at_exits"
                                        :form="form"
                                        label="Are fire alarm call points located at all final exits, storey exits, fire compartment wall exits or exits from rooms of high risk?"
                                        number="21.02"
                                    />
                                    <risk-row
                                        id="fire_control_panel_at_exit"
                                        :form="form"
                                        label="Is the fire control panel located at an exit and visible to the emergency services from the outside of the building?"
                                        number="21.03"
                                    />
                                    <risk-row
                                        id="fire_discovered_quickly"
                                        :form="form"
                                        label="Can the existing means of detection ensure a fire is discovered quickly enough for the alarm to be raised in time for all the occupants to escape to a place of total safety?"
                                        number="21.04"
                                    />
                                    <risk-row
                                        id="correct_fire_detectors"
                                        :form="form"
                                        label="Are the automatic fire detectors of the right type and in the appropriate locations?"
                                        number="21.05"
                                    />
                                    <risk-row
                                        id="warning_clearly_heard"
                                        :form="form"
                                        label="Can the means of warning be clearly heard or be perceptible and understood by everyone throughout the whole building when initiated from a single point?"
                                        number="21.06"
                                    />
                                    <risk-row
                                        id="provisions_where_cannot_be_heard"
                                        :form="form"
                                        label="Are there provisions for people in locations where the alarm cannot be heard?"
                                        number="21.07"
                                    />
                                    <risk-row
                                        id="detection_warnings_backup_power"
                                        :form="form"
                                        label="If the fire-detection and warning system is electrically powered, does it have a back-up power supply?"
                                        number="21.08"
                                    />
                                    <risk-row
                                        id="remote_alarm_transmission"
                                        :form="form"
                                        label="Is there a remote transmission of alarm signal provided where applicable?"
                                        number="21.09"
                                    />
                                    <risk-row
                                        id="anti_tamper_device_for_alarms"
                                        :form="form"
                                        label="Are anti-tamper devices fitted to fire alarm call points in post 2016 fitted systems."
                                        number="21.10"
                                    />
                                    <risk-row
                                        id="zone_plan_adjacent_control_panel"
                                        :form="form"
                                        label="Is a zone plan of the fire alarm system provided adjacent to the main control panel in newer installations?"
                                        number="21.11"
                                    />
                                </tbody>
                            </table>
                            <text-row
                                id="giving_warning_relevant_info"
                                wide
                                v-model="form.giving_warning_relevant_info"
                                label="Relevant information (including description of arrangements and deficiencies observed):"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h5>22.00 Manual fire extinguisher appliances</h5>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <table class="table">
                                <tbody>
                                    <risk-row
                                        id="manual_fire_extinguishers"
                                        :form="form"
                                        label="Is there reasonable provision of manual fire extinguishing appliances?"
                                        number="22.01"
                                    />
                                    <risk-row
                                        id="fire_extinguishers_suitable"
                                        :form="form"
                                        label="Are the fire extinguishers suitable for the purpose?"
                                        number="22.02"
                                    />
                                    <risk-row
                                        id="enough_extinguishers"
                                        :form="form"
                                        label="Are there enough extinguishers sited throughout the premises at appropriate locations?"
                                        number="2.03"
                                    />
                                    <risk-row
                                        id="correct_extinguishers_close_to_hazards"
                                        :form="form"
                                        label="Are the right types of extinguishers located close to the fire hazards and can users get to them without exposing themselves to risk?"
                                        number="22.04"
                                    />
                                    <risk-row
                                        id="avoidance_extinguishers_from_view"
                                        :form="form"
                                        label="Is there avoidance of extinguishers hidden from view so that their position need indicating?"
                                        number="22.05"
                                    />
                                    <risk-row
                                        id="prevented_misuses_fire_extinguishers"
                                        :form="form"
                                        label="Have steps been taken to prevent the misuse of extinguishers where appropriate?"
                                        number="22.06"
                                    />
                                    <risk-row
                                        id="fire_fighting_applicances_checked"
                                        :form="form"
                                        label="Are firefighting appliances checked that they are in good order at least monthly by the occupier?"
                                        number="22.07"
                                    />
                                    <risk-row
                                        id=""
                                        :form="form"
                                        label="Are those who test and maintain the firefighting equipment competent to do so?"
                                        number="22.08"
                                    />
                                    <risk-row
                                        id="hose_reels_maintained"
                                        :form="form"
                                        label="Are hose reels provided where required and suitably maintained?"
                                        number="22.09"
                                    />
                                </tbody>
                            </table>
                            <text-row
                                id="manual_fire_appliances_relevant_info"
                                wide
                                v-model="form.manual_fire_appliances_relevant_info"
                                label="Relevant information (including description of arrangements and deficiencies observed):"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h5>23.00 Relevant automatic fire extinguishing systems</h5>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <table class="table">
                                <tbody>
                                    <risk-row
                                        id="suitable_sprinkler_system"
                                        :form="form"
                                        label="Is there a suitable sprinkler or water-based systems provided where required for the risk present?"
                                        number="23.01"
                                    />
                                    <risk-row
                                        id="gaseous_local_application"
                                        :form="form"
                                        label="Is there a suitable gaseous local application or total flooding systems provided where required for the risk?"
                                        number="23.02"
                                    />
                                    <risk-row
                                        id="competent_testers"
                                        :form="form"
                                        label="Are those who test and maintain the fixed firefighting systems competent to do so?"
                                        number="23.03"
                                    />
                                </tbody>
                            </table>
                            <text-row
                                id="automatic_fire_systems_relevant_info"
                                wide
                                v-model="form.automatic_fire_systems_relevant_info"
                                label="Relevant information (including description of arrangements and deficiencies observed):"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h5>24.00 Other relevant fixed systems and equipment</h5>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <table class="table">
                                <tbody>
                                    <risk-row
                                        id="type_fixed_system"
                                        :form="form"
                                        label="Type of fixed system"
                                        number="24.01"
                                    />
                                    <risk-row
                                        id="suitable_fire_fighter_switches"
                                        :form="form"
                                        label="Is there suitable provision of firefighters’ switch(es) for high voltage luminous tube signs, etc.?"
                                        number="24.02"
                                    />
                                    <risk-row
                                        id="notice_for_luminous_discharge_tubes"
                                        :form="form"
                                        label="Has notice been given to the fire and rescue authority regarding luminous discharge tubes cut-off switch, its colouring and marking?"
                                        number="24.03"
                                    />
                                    <risk-row
                                        id="electrical_isolation_pv_cells"
                                        :form="form"
                                        label="Are there appropriately sited facilities for electrical isolation and any photovoltaic (PV) cells, with appropriate signage, to assist the fire and rescue service?"
                                        number="24.04"
                                    />
                                </tbody>
                            </table>
                            <text-row
                                id="fixed_systems_relevant_info"
                                wide
                                v-model="form.fixed_systems_relevant_info"
                                label="Relevant information (including description of arrangements and deficiencies observed):"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h5>25.00 Procedures and Arrangements</h5>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <table class="table">
                                <tbody>
                                    <risk-row
                                        id="competent_person_is"
                                        :form="form"
                                        label="Safety assistance: The competent person(s) appointed under Article 18 of the Fire Safety Order to assist the responsible person in undertaking the preventive and protective measures (i.e. relevant general fire precautions) is"
                                        number="25.01"
                                    />
                                    <risk-row
                                        id="fire_safety_premises_managed_by"
                                        :form="form"
                                        label="Fire safety at the premises is managed by?"
                                        number="25.02"
                                    />
                                    <risk-row
                                        id="procedures_documented"
                                        :form="form"
                                        label="Are procedures in the event of a fire appropriate and properly documented, where appropriate13)?"
                                        number="25.03"
                                    />
                                    <risk-row
                                        id="staff_aware_emergancy_plan"
                                        :form="form"
                                        label="Are staff aware of the emergency plan, including the identity of people nominated to do a particular fire duty task?"
                                        number="25.04"
                                    />
                                    <risk-row
                                        id="guests_visitors_informed"
                                        :form="form"
                                        label="Have visitors and guests been informed about what to do in an emergency?"
                                        number="25.05"
                                    />
                                    <risk-row
                                        id="staff_informed_dangerous_substances"
                                        :form="form"
                                        label="Have staff been given information about any dangerous substances?"
                                        number="25.06"
                                    />
                                    <risk-row
                                        id="temp_staff_informed"
                                        :form="form"
                                        label="Do you have arrangements for informing temporary or agency staff?"
                                        number="25.07"
                                    />
                                    <risk-row
                                        id="guest_staff_informed"
                                        :form="form"
                                        label="Is there an arrangement for informing other employers whose staff are guest workers in your premises, such as maintenance contractors and cleaners?"
                                        number="25.08"
                                    />
                                    <risk-row
                                        id="arrangements_with_other_responsible_persons"
                                        :form="form"
                                        label="Have fire safety arrangements been co-ordinated with other responsible people and with any contractors in the building?"
                                        number="25.09"
                                    />
                                    <risk-row
                                        id="information_recorded_for_cooperation"
                                        :form="form"
                                        label="Have details been recorded of any information or instructions you have given and the details of any arrangements for co-operation and co-ordination with others?"
                                        number="25.10"
                                    />
                                    <risk-row
                                        id="fire_safety_manager_appointed"
                                        :form="form"
                                        label="Has a fire safety manager/deputy fire safety manager been appointed in writing with a suitable job description?"
                                        number="25.11"
                                    />
                                    <risk-row
                                        id="fire_wardens_appointed"
                                        :form="form"
                                        label="Have sufficient fire wardens been appointed to help in evacuating the premises?"
                                        number="25.12"
                                    />
                                    <risk-row
                                        id="summoning_fire_rescue_arrangements"
                                        :form="form"
                                        label="Are there suitable arrangements for summoning the fire and rescue service?"
                                        number="25.13"
                                    />
                                    <risk-row
                                        id="fire_rescue_handover_arrangements"
                                        :form="form"
                                        label="Are there suitable arrangements to meet the fire and rescue service on arrival and provide them with relevant information?"
                                        number="25.14"
                                    />
                                    <risk-row
                                        id="evacuation_arrangements"
                                        :form="form"
                                        label="Are there suitable arrangements for ensuring that the premises have been evacuated?"
                                        number="25.15"
                                    />
                                    <risk-row
                                        id="fire_assembly_points"
                                        :form="form"
                                        label="Is there a suitable fire assembly point(s)?"
                                        number="25.16"
                                    />
                                    <risk-row
                                        id="disabled_evacuation_procedures"
                                        :form="form"
                                        label="Are there adequate procedures for evacuation of any disabled people who are likely to be present?"
                                        number="25.17"
                                    />
                                    <risk-row
                                        id="persons_trained_to_assist_disabled_evac"
                                        :form="form"
                                        label="Are persons nominated and trained to assist with evacuation of disabled people?"
                                        number="25.18"
                                    />
                                    <risk-row
                                        id="persons_trained_fire_extinguishers"
                                        :form="form"
                                        label="Are persons nominated and trained to use fire extinguishing appliances?"
                                        number="25.19"
                                    />
                                    <risk-row
                                        id="fire_rescue_pre_fire_inspection"
                                        :form="form"
                                        label="Have the fire and rescue service carried out pre-fire inspections of the premises?"
                                        number="25.20"
                                    />
                                    <risk-row
                                        id="fire_rescue_emergency_pack_provided"
                                        :form="form"
                                        label="Has an emergency pack been provided for fire and rescue service information during an incident?"
                                        number="25.21"
                                    />
                                    <risk-row
                                        id="routine_inspections"
                                        :form="form"
                                        label="Are routine fire inspections carried out?"
                                        number="25.22"
                                    />
                                </tbody>
                            </table>
                            <text-row
                                id="procedures_relevant_info"
                                wide
                                v-model="form.procedures_relevant_info"
                                label="Relevant information (including description of arrangements and deficiencies observed):"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h5>26.00 Training and drills</h5>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <table class="table">
                                <tbody>
                                    <risk-row
                                        id=""
                                        :form="form"
                                        label="Are all staff given adequate fire safety instruction and training on induction?"
                                        number="26.01"
                                    />
                                    <risk-row
                                        id=""
                                        :form="form"
                                        label="Are all staff given periodic refresher fire training at suitable intervals?"
                                        number="26.02"
                                    />
                                    <risk-row
                                        id=""
                                        :form="form"
                                        label="Are regular fire drills carried out at appropriate intervals?"
                                        number="26.03"
                                    />

                                    <risk-row
                                        id=""
                                        :form="form"
                                        label="Fire risks in the premises?"
                                        number="A"
                                    />
                                    <risk-row
                                        id=""
                                        :form="form"
                                        label="The fire safety measures on the premises?"
                                        number="B"
                                    />
                                    <risk-row
                                        id=""
                                        :form="form"
                                        label="Action in case of fire?"
                                        number="C"
                                    />
                                    <risk-row
                                        id=""
                                        :form="form"
                                        label="Action on hearing the fire alarm signal?"
                                        number="D"
                                    />
                                    <risk-row
                                        id=""
                                        :form="form"
                                        label="Method of operation of fire alarm call points?"
                                        number="E"
                                    />
                                    <risk-row
                                        id=""
                                        :form="form"
                                        label="Location and use of fire extinguishers?"
                                        number="F"
                                    />
                                    <risk-row
                                        id=""
                                        :form="form"
                                        label="Means for summoning the fire and rescue service?"
                                        number="G"
                                    />
                                    <risk-row
                                        id=""
                                        :form="form"
                                        label="Identity of persons nominated to assist with evacuation?"
                                        number="H"
                                    />
                                    <risk-row
                                        id=""
                                        :form="form"
                                        label="Identity of persons nominated to use fire extinguishing appliances?"
                                        number="I"
                                    />
                                    <risk-row
                                        id=""
                                        :form="form"
                                        label="Are fire wardens given additional training?"
                                        number="26.05"
                                    />
                                    <risk-row
                                        id=""
                                        :form="form"
                                        label="Are joint training and fire drills sessions carried out in multi-occupied buildings?"
                                        number="26.06"
                                    />
                                    <risk-row
                                        id=""
                                        :form="form"
                                        label="Are employers aware of fire risks and general fire precautions for other organisations in the premises?"
                                        number="26.07"
                                    />
                                    <risk-row
                                        id=""
                                        :form="form"
                                        label="Are employees provided with adequate instructions and information regarding fire risks and general fire precautions for other organisations in the premises?"
                                        number="26.08"
                                    />
                                    <risk-row
                                        id=""
                                        :form="form"
                                        label="Have staff working in areas for used or store hazardous or explosive substances had appropriate fire training?"
                                        number="26.09"
                                    />
                                    <risk-row
                                        id=""
                                        :form="form"
                                        label="Has the fire manager/deputy fire manager had appropriate training for their role?"
                                        number="26.10"
                                    />
                                    <risk-row
                                        id=""
                                        :form="form"
                                        label="Are appropriate training measures in place for those workers with language difficulties?"
                                        number="26.11"
                                    />
                                    <risk-row
                                        id=""
                                        :form="form"
                                        label="Is the content of training provided considered adequate14)?"
                                        number="26.12"
                                    />
                                </tbody>
                            </table>
                            <text-row
                                id="training_drills_relevant_info"
                                wide
                                v-model="form.training_drills_relevant_info"
                                label="Relevant information (including description of arrangements and deficiencies observed):"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h5>27.00 Testing and maintenance</h5>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <table class="table">
                                <tbody>
                                    <risk-row
                                        id="adequate_premises_maintenance"
                                        :form="form"
                                        label="Is there adequate maintenance of the premises?"
                                        number="27.01"
                                    />
                                    <risk-row
                                        id="testing_and_periodic_test_maintenance_fire_detection"
                                        :form="form"
                                        label="Weekly testing and periodic servicing of fire detection and alarm system undertaken?"
                                        number="27.02"
                                    />
                                    <risk-row
                                        id="testing_routines_elighting"
                                        :form="form"
                                        label="Monthly and annual testing routines for emergency lighting?"
                                        number="27.03"
                                    />
                                    <risk-row
                                        id="annual_maintenance_fire_ex_applicances"
                                        :form="form"
                                        label="Annual maintenance of fire extinguishing appliances?"
                                        number="27.04"
                                    />
                                    <risk-row
                                        id="testing_inspection_sprinklers"
                                        :form="form"
                                        label="Are weekly testing and periodic inspection of sprinkler installations where provided?"
                                        number="27.05"
                                    />
                                    <risk-row
                                        id="inspection_testing_dry_riser"
                                        :form="form"
                                        label="Are six-monthly inspection and annual testing of dry riser where required?"
                                        number="27.06"
                                    />
                                    <risk-row
                                        id="testing_inspection_fire_fighting_lifts"
                                        :form="form"
                                        label="Are weekly and monthly testing, six monthly inspection and annual testing of fire-fighting lifts required where provided?"
                                        number="27.07"
                                    />
                                    <risk-row
                                        id="inspecion_escape_staircases"
                                        :form="form"
                                        label="Are periodic inspection of external escape staircase and gangways where provided?"
                                        number="27.08"
                                    />
                                    <risk-row
                                        id="checks_exit_doors"
                                        :form="form"
                                        label="Routine checks of final exit doors and security fastenings?"
                                        number="27.09"
                                    />
                                    <risk-row
                                        id="inspection_lighting_protection_systems"
                                        :form="form"
                                        label="Is an annual inspection of lightning protection system required where provided?"
                                        number="27.10"
                                    />
                                    <risk-row
                                        id="inspections_testing_hydrants"
                                        :form="form"
                                        label="Are six-monthly inspection and annual testing of private hydrants required where provided?"
                                        number="27.11"
                                    />
                                    <risk-row
                                        id="other_relevant_inspections"
                                        :form="form"
                                        label="Are other relevant inspections or tests where required?"
                                        number="27.12"
                                    />
                                    <risk-row
                                        id="reporting_systems_for_failed_safety_measures"
                                        :form="form"
                                        label="Are suitable systems in place for reporting and subsequent restoration of fire safety measures that have failed?"
                                        number="27.13"
                                    />
                                </tbody>
                            </table>
                            <text-row
                                id="testing_maintenance_relevant_info"
                                wide
                                v-model="form.testing_maintenance_relevant_info"
                                label="Relevant information (including description of arrangements and deficiencies observed):"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h5>28.00 Records</h5>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <table class="table">
                                <tbody>
                                    <risk-row
                                        id="training_drills_records"
                                        :form="form"
                                        label="Are appropriate records kept for fire training and fire evacuation drills?"
                                        number="28.01"
                                    />
                                    <risk-row
                                        id="fire_alarm_test_records"
                                        :form="form"
                                        label="Are appropriate records kept for fire alarm tests?"
                                        number="28.02"
                                    />
                                    <risk-row
                                        id="escape_lighting_records"
                                        :form="form"
                                        label="Are appropriate records kept for escape lighting tests?"
                                        number="28.03"
                                    />
                                    <risk-row
                                        id="maintenance_records"
                                        :form="form"
                                        label="Are appropriate records kept for the maintenance and testing of other fire protection systems?"
                                        number="28.04"
                                    />
                                    <risk-row
                                        id="fire_records_available"
                                        :form="form"
                                        label="Are your fire records immediately available for inspection by the enforcing authority?"
                                        number="28.05"
                                    />
                                    <risk-row
                                        id="emergency_plan_in_place"
                                        :form="form"
                                        label="Is there an emergency plan in place?"
                                        number="28.06"
                                    />
                                    <risk-row
                                        id="emergency_plan_accounts_other_plans"
                                        :form="form"
                                        label="Does the fire emergency plan take account of other emergency plans applicable in the building?"
                                        number="28.07"
                                    />
                                    <risk-row
                                        id="emergence_plan_available_for_staff"
                                        :form="form"
                                        label="Is the fire emergency plan readily available for staff to read?"
                                        number="28.08"
                                    />
                                    <risk-row
                                        id="emergency_plan_for_authority"
                                        :form="form"
                                        label="Is the fire emergency plan available to the enforcing authority?"
                                        number="28.09"
                                    />
                                    <risk-row
                                        id="peeps_required"
                                        :form="form"
                                        label="Are Personal Emergency Evacuation Plans (PEEPS) required and in place?"
                                        number="28.10"
                                    />
                                </tbody>
                            </table>
                            <text-row
                                id="recording_relevant_info"
                                wide
                                v-model="form.recording_relevant_info"
                                label="Relevant information (including description of arrangements and deficiencies observed):"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h5>
                                29.00 Young persons - Have the following matters been taken into account in the risk
                                assessment of young persons?
                            </h5>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <table class="table">
                                <tbody>
                                    <risk-row
                                        id="young_persons_risks"
                                        :form="form"
                                        label="The inexperience, lack of awareness of risks and immaturity of young persons"
                                        number="29.01"
                                    />
                                    <risk-row
                                        id="young_persons_layout"
                                        :form="form"
                                        label="The fitting-out and layout of the premises"
                                        number="29.02"
                                    />
                                    <risk-row
                                        id="young_person_exposure_to_agents"
                                        :form="form"
                                        label="The nature, degree and duration of exposure to physical and chemical agents"
                                        number="29.03"
                                    />
                                    <risk-row
                                        id="young_persons_work_equipment"
                                        :form="form"
                                        label="The form, range, and use of work equipment and the way in which it is handled"
                                        number="29.04"
                                    />
                                    <risk-row
                                        id="young_persons_organisation_processes"
                                        :form="form"
                                        label="The organisation of processes and activities"
                                        number="29.05"
                                    />
                                    <risk-row
                                        id="young_persons_training"
                                        :form="form"
                                        label="The extent of the safety training provided or to be provided to young persons"
                                        number="29.06"
                                    />
                                    <risk-row
                                        id="young_persons_risks_protection"
                                        :form="form"
                                        label="Risks from agents, processes and work listed in the Annex to Council Directive 94/33/EC on the protection of young people at work"
                                        number="29.07"
                                    />
                                </tbody>
                            </table>
                            <text-row
                                id="_relevant_info"
                                wide
                                v-model="form._relevant_info"
                                label="Relevant information (including description of arrangements and deficiencies observed):"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <table class="table text-center legend-table">
                                <tr>
                                    <td
                                        class="font-weight-bold"
                                        rowspan="2"
                                    >
                                        Likelihood of Fire
                                    </td>
                                    <td
                                        class="font-weight-bold"
                                        colspan="3"
                                    >
                                        Potential Consequences of Fire
                                    </td>
                                </tr>
                                <tr>
                                    <td class="bg-success font-weight-bold">
                                        Slight Harm
                                    </td>
                                    <td class="bg-warning font-weight-bold">
                                        Moderate Harm
                                    </td>
                                    <td class="bg-danger font-weight-bold">
                                        Extreme Harm
                                    </td>
                                </tr>
                                <tr>
                                    <td class="bg-success font-weight-bold">
                                        Low
                                    </td>
                                    <td>Trivial Risk</td>
                                    <td>Tolerable Risk</td>
                                    <td>Moderate Risk</td>
                                </tr>
                                <tr>
                                    <td class="bg-warning font-weight-bold">
                                        Medium
                                    </td>
                                    <td>Tolerable Risk</td>
                                    <td>Moderate Risk</td>
                                    <td>Substantial Risk</td>
                                </tr>
                                <tr>
                                    <td class="bg-danger font-weight-bold">
                                        High
                                    </td>
                                    <td>Moderate Risk</td>
                                    <td>Substantial Risk</td>
                                    <td>Intolerable Risk</td>
                                </tr>
                            </table>
                        </div>
                    </div>
                    <select-row
                        id="likelihood_of_fire"
                        v-model="form.likelihood_of_fire"
                        :options="['Low', 'Medium', 'High']"
                        label="Considering the fire prevention measures observed at the time of this risk assessment, it is considered that the hazard from fire (likelihood of fire) at these premises is:"
                    />
                    <div class="row">
                        <div class="col-12">
                            <p>In this context, a definition of the above terms is as follows:</p>
                            <table class="table legend-table">
                                <tr>
                                    <td class="font-weight-bold text-center">
                                        Low
                                    </td>
                                    <td>
                                        Unusually low likelihood of fire because of negligible potential sources of
                                        ignition.
                                    </td>
                                </tr>
                                <tr>
                                    <td class="font-weight-bold text-center">
                                        Medium
                                    </td>
                                    <td>
                                        Normal fire hazards (e.g. potential ignition sources) for this type of
                                        occupancy, with fire hazards generally subject to appropriate controls (other
                                        than minor shortcomings).
                                    </td>
                                </tr>
                                <tr>
                                    <td class="font-weight-bold text-center text-danger">
                                        High
                                    </td>
                                    <td class="text-danger">
                                        Lack of adequate controls applied to one or more significant fire hazards, such
                                        as to result in significant increase in likelihood of fire.
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </div>
                    <select-row
                        id="life_safety"
                        v-model="form.life_safety"
                        :options="['Slight Harm', 'Moderate Harm', 'Extreme Harm']"
                        label="Considering the nature of the premises and the occupants, as well as the fire protection and procedural arrangements observed at the time of this fire risk assessment, it is considered that the consequences for life safety in the event of fire would be:"
                    />
                    <div class="row">
                        <div class="col-12">
                            <p>In this context, a definition of the above terms is as follows:</p>
                            <table class="table legend-table">
                                <tr>
                                    <td class="font-weight-bold text-center">
                                        Slight Harm
                                    </td>
                                    <td>
                                        Outbreak of fire unlikely to result in serious injury or death of any occupant
                                        (other than an occupant sleeping in a room in which a fire occurs).
                                    </td>
                                </tr>
                                <tr>
                                    <td class="font-weight-bold text-center">
                                        Moderate Harm
                                    </td>
                                    <td>
                                        Outbreak of fire could result in injury (including serious injury) of one or
                                        more occupants, but it is unlikely to involve multiple fatalities.
                                    </td>
                                </tr>
                                <tr>
                                    <td class="font-weight-bold text-center text-danger">
                                        Extreme Harm
                                    </td>
                                    <td class="text-danger">
                                        Significant potential for serious injury or death of one or more occupants.
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </div>
                    <select-row
                        id="risk_of_life"
                        v-model="form.risk_of_life"
                        :options="['Trivial', 'Tolerable', 'Moderate', 'Substantial', 'Intolerable']"
                        label="Accordingly, it is considered that the risk to life from fire at these premises is:"
                    />

                    <table class="table">
                        <thead>
                            <tr>
                                <th>Risk Level</th>
                                <th>Action and Timescale</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Trivial</td>
                                <td>
                                    <p>No action is required and no detailed records need be kept.</p>
                                </td>
                            </tr>
                            <tr>
                                <td>Tolerable</td>
                                <td>
                                    <p>
                                        No major additional fire precautions required. However, there might be a need for
                                        reasonably practicable improvements that involve minor or limited cost.
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <td>Moderate</td>
                                <td>
                                    <p>
                                        It is essential that efforts are made to reduce the risk. Risk reduction measures,
                                        which should take cost into account, should be implemented within a defined time
                                        period. Where moderate risk is associated with consequences that constitute extreme
                                        harm, further assessment might be required to establish more precisely the
                                        likelihood of harm as a basis for determining the priority for improved control
                                        measures.
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <td>Substantial</td>
                                <td>
                                    <p>
                                        Considerable resources might have to be allocated to reduce the risk. If the
                                        premises
                                        are unoccupied, it should not be occupied until the risk has been reduced. If the
                                        premises are occupied, urgent action should be taken.
                                    </p>
                                </td>
                            </tr>
                            <tr>
                                <td>Intolerable</td>
                                <td>
                                    <p>Premises (or relevant area) should not be occupied until the risk is reduced.</p>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <p class="text-danger">
                        (Note that, although the purpose of this section is to place the fire risk in context, the above
                        approach to fire risk assessment is subjective and for guidance only. All hazards and
                        deficiencies identified in this report should be addressed by implementing all recommendations
                        contained in the following action plan. The fire risk assessment should be reviewed regularly.
                    </p>
                    <select-row
                        id="risk_of_life"
                        v-model="form.recommendations_fire_risk_level"
                        :options="['Trivial', 'Tolerable',]"
                        label="It is considered that the following recommendations should be implemented to reduce fire risk to, or maintain it at, the following level:"
                    />
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h3>Comments</h3>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <textarea
                                v-model="form.comments"
                                class="form-control"
                                placeholder="Main areas for concern are..."
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h3>Definition of Priorities (where applicable)</h3>
                            <table class="table">
                                <tbody>
                                    <tr>
                                        <th>High</th>
                                        <td>
                                            <p>
                                                Breaches of legal requirements, which could cause injury and require
                                                immediate short-term action. Also, includes matters that can be resolved at
                                                minimal cost
                                            </p>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Medium</th>
                                        <td>
                                            <p>
                                                Breaches in legislation that may require medium/long term action to resolve
                                            </p>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Low</th>
                                        <td>
                                            <p>
                                                Items of non-urgent priority or for future consideration.
                                            </p>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <table class="table">
                        <thead>
                            <tr>
                                <th style="width: 8rem">
                                    Ref No.
                                </th>
                                <th>Recommended Action Required</th>
                                <th style="width: 10rem">
                                    Priority
                                </th>
                                <th style="width: 10rem">
                                    Timescale
                                </th>
                                <td style="width: 2rem">
                                &nbsp;
                                </td>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(item, index) in form.action_plan_items"
                                :key="index"
                            >
                                <td>
                                    <input
                                        type="text"
                                        class="form-control"
                                        v-model="item.ref"
                                    >
                                </td>
                                <td>
                                    <input
                                        type="text"
                                        class="form-control"
                                        v-model="item.plan"
                                    >
                                </td>
                                <td>
                                    <select
                                        v-model="item.priority"
                                        class="form-control"
                                    >
                                        <option>High</option>
                                        <option>Medium</option>
                                        <option>Low</option>
                                    </select>
                                </td>
                                <td>
                                    <select
                                        v-model="item.timescale"
                                        class="form-control"
                                    >
                                        <option>Immediate</option>
                                        <option>Short Term</option>
                                        <option>Medium Term</option>
                                        <option>Long Term</option>
                                    </select>
                                </td>
                                <td>
                                    <button
                                        @click.prevent="removeActionPlanItem(index)"
                                        class="btn btn-sm btn-outline-danger"
                                    >
                                        <i class="fa fa-times" />
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <td
                                    class="text-right"
                                    colspan="6"
                                >
                                    <button
                                        class="btn btn-primary"
                                        type="button"
                                        @click="addActionPlanItem()"
                                    >
                                        Add item
                                    </button>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            <form-photos :form-id="form.id" />

            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-12">
                            <h3>Signoff</h3>
                            <p>I declare that I am a competent person (CP) in Fire Risk Assessments. </p>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-sm-8">
                            <label class="top">Engineer Signature:</label>
                            <br>
                            <user-select
                                v-model="form.engineer_id"
                                :admins="false"
                                :office="false"
                            />
                        </div>
                        <div class="col-sm-12 col-md-6 col-lg-4 form-group">
                            <label
                                class="top"
                                for="engineer_sign_date"
                            >Date</label>
                            <br>
                            <date-picker
                                id="engineer_sign_date"
                                v-model="form.engineer_sign_date"
                                :options="{format: 'L'}"
                            />
                            <error
                                :errors="errors"
                                name="engineer_sign_date"
                            />
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-12">
                            <p>
                                Accepted, For and behalf of the user, by Responsible person (RP). I understand that
                                information proved at the time of this assessment was accurate.
                            </p>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-12 col-lg-4">
                            <label class="top">Customer Signature:</label>
                            <br>
                            <signature
                                v-if="!form.customer_signature"
                                ref="customerSignature"
                            />
                            <img
                                v-if="form.customer_signature"
                                :src="form.customer_signature"
                                alt="Customer signature"
                            >
                        </div>
                        <div class="col-sm-12 col-md-6 col-lg-4 form-group">
                            <label
                                class="top"
                                for="customer_sign_name"
                            >Customer Name</label>
                            <br>
                            <input
                                id="customer_sign_name"
                                v-model="form.customer_sign_name"
                                class="form-control"
                                name="customer_sign_name"
                                placeholder="Customer Name"
                                type="text"
                            >
                            <error
                                :errors="errors"
                                name="customer_sign_name"
                            />
                        </div>
                        <div class="col-sm-12 col-md-6 col-lg-4 form-group">
                            <label
                                class="top"
                                for="customer_sign_date"
                            >Date</label>
                            <br>
                            <date-picker
                                id="customer_sign_date"
                                v-model="form.customer_sign_date"
                                :options="{format: 'L'}"
                            />
                            <error
                                :errors="errors"
                                name="customer_sign_date"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-body">
                    <div class="row form-group">
                        <div class="col-12">
                            <button
                                :disabled="isSaving || isLoading"
                                class="btn btn-success"
                                type="submit"
                            >
                                Save
                            </button>

                            <button
                                v-if="form.id && canDelete"
                                :disabled="isSaving || isLoading"
                                class="btn btn-outline-danger"
                                type="button"
                                @click="deleteForm()"
                            >
                                <i class="fa fa-times" /> Delete
                            </button>

                            <a
                                v-if="form.id"
                                :href="'/api/forms/' + form.id + '/stream'"
                                class="btn btn-info"
                                target="_blank"
                            >
                                <i class="fa fa-print" /> Preview/Print
                            </a>

                            <a
                                v-if="form.id"
                                :download="form.doc_no + '.pdf'"
                                :href="'/api/forms/' + form.id + '/download'"
                                class="btn btn-info"
                                target="_blank"
                            >
                                <i class="fa fa-file" /> Download PDF
                            </a>

                            <router-link
                                v-if="form.id"
                                :to="{name: 'office.emails.new', params: {customer_id: form.customer_id}, query: {form_id: form.id}}"
                                class="btn btn-info"
                            >
                                <i class="fa fa-envelope" /> Send to Customer
                            </router-link>

                            <router-link
                                v-if="$route.name === 'engineer.forms.edit' && form.job_id"
                                :to="{name: 'engineer.jobs.show', params: {id: form.job_id}}"
                                class="btn btn-info"
                            >
                                <i class="fa fa-chevron-left" /> Back to Job
                            </router-link>

                            <router-link
                                v-if="$route.name === 'office.forms.edit' && form.job_id"
                                :to="{name: 'office.jobs.show', params: {id: form.job_id}}"
                                class="btn btn-info"
                            >
                                <i class="fa fa-briefcase" /> View Job
                            </router-link>

                            <customer-site-footer-links
                                :customer-id="form.customer_id"
                                :site-id="form.site_id"
                            />

                            <activity-log
                                v-if="form.id"
                                :id="form.id"
                                model="Form"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import moment from "moment";
import DatePicker from "../DateTimePickerComponent.vue";
import JobInfo from "./parts/JobInfoComponent";
import CustomerSearch from "../CustomerSearch";
import Service from "../../services/FormService";
import JobService from "../../services/JobService";
import AuthService from "../../services/AuthService";
import SiteService from "../../services/SitesService";
import TextRow from "./parts/TextRowComponent";
import SelectRow from "./parts/SelectRowComponent";
import DateRow from "./parts/DateRowComponent";
import RiskRow from "./parts/RiskRowComponent";
import Signature from "../SignatureComponent.vue";
import UsesErrors from "../../mixins/UsesErrors";
import UsesInfoMessage from "../../mixins/UsesInfoMessage";
import Sortables from "../../mixins/Sortables";
import SettingsMixin from "../../mixins/SystemSettings";
import UsesFormRouteQuery from "../../mixins/UsesFormRouteQuery";
import FormPhotos from "./parts/FormPhotosComponent.vue";
import DurationSelectComponent from "./parts/DurationSelectComponent.vue";

const initialActionPlanItem = {
    ref: "",
    plan: "",
    priority: "",
    timescale: "",
};

const initialForm = {
    action_plan_items: [{...initialActionPlanItem}],
};

export default {
    components: {
        DurationSelectComponent,
        JobInfo,
        CustomerSearch,
        TextRow,
        SelectRow,
        DateRow,
        RiskRow,
        DatePicker,
        Signature,
        FormPhotos,
    },
    mixins: [UsesErrors, UsesInfoMessage, Sortables, SettingsMixin, UsesFormRouteQuery],
    data() {
        return {
            form: {...initialForm},
            jobs: [],
            token: undefined,
        };
    },
    computed: {
        companyName: () => window.SystemSettings.company_name,
        canDelete: function () {
            return (
                AuthService.user.admin_access || AuthService.user.office_access
            );
        },
    },
    mounted: function () {
        this.populateFormFromRouteQuery();

        if (this.$route.params.id) {
            const id = this.$route.params.id;
            this.loadForm(id);
        } else {
            Service.nextNo().then((res) => {
                this.form.doc_no = res.data;
            });
        }

    },
    methods: {
        loadForm: function (id) {
            this.setLoading();
            Service.get(id)
                .then((res) => {
                    const form = res.data;
                    if (!form.customer) {
                        form.customer = {
                            company_name: "",
                        };
                    }
                    if (res.data.engineer_sign_date) {
                        form.engineer_sign_date = moment(
                            res.data.engineer_sign_date
                        );
                    } else {
                        form.engineer_sign_date = moment();
                    }
                    if (res.data.customer_sign_date) {
                        form.customer_sign_date = moment(
                            res.data.customer_sign_date
                        );
                    } else {
                        form.engineer_sign_date = moment();
                    }
                    form.last_assesment_date = res.data.last_assesment_date ? moment(res.data.last_assesment_date) : '';
                    this.form = form;
                    this.getJobs();
                    this.siteChanged();
                })
                .catch((err) => {
                    this.setErrorMessage("Could not load form");
                })
                .then(() => {
                    this.clearLoading();
                });
        },
        customerChanged: function (customer) {
            this.form.customer = customer ? customer : {};
            if (this.form.customer) {
                this.form.customer_id = this.form.customer.id;
            } else {
                this.form.customer_id = null;
            }
            if (customer && customer.sites) {
                if (
                    !customer.sites
                        .map((site) => site.id)
                        .includes(this.form.site_id)
                ) {
                    this.form.site_id = customer.sites[0].id;
                    this.siteChanged();
                }
            } else {
                this.form.site_id = null;
            }
            this.getJobs();
        },
        siteChanged: function () {
            this.loadSiteFormFields();
        },
        loadSiteFormFields: function () {
            if (
                typeof this.form.site_id === "undefined" ||
                !this.form.site_id
            ) {
                return;
            }

            SiteService.get(this.form.site_id).then((res) => {
                if (res.data.form_fields) {
                    const siteServiceTime = res.data.form_fields.find(
                        (f) => f.name === "ra_service_time"
                    );
                    this.form.ra_service_time =
                        this.form.ra_service_time || siteServiceTime
                            ? siteServiceTime.value
                            : "";
                }
            });
        },
        onJobLoadedFromRoute() {
            this.siteChanged();
            this.getJobs();
        },
        getJobs: function () {
            if (!this.form.customer_id) {
                this.jobs = [];
                this.form.job_id = null;
                return;
            }
            JobService.search({
                "filter[customer_id]": this.form.customer_id,
            }).then(
                (res) => {
                    this.jobs = res.data.data || [];
                },
                (err) => {
                    this.setErrorMessage("Could not load customer jobs");
                    this.jobs = [];
                }
            );
        },
        saveForm: function () {
            this.setSaving();
            if (
                this.$refs.customerSignature &&
                !this.$refs.customerSignature.isEmpty()
            ) {
                this.form.customer_signature =
                    this.$refs.customerSignature.toPng();
            }
            let data = {...this.form};
            data.type = "fire_risk_assessment";
            delete data["customer"];
            delete data["site"];
            delete data["job"];
            delete data["data"];
            data.action_plan_items = data.action_plan_items.filter(
                (i) => i.requirement !== ""
            );

            if (this.form.id) {
                Service.update(data)
                    .then((res) => {
                        this.clearSaving();
                        this.setInfoMessage("Form saved successfully");
                        this.$emit("formSaved", res.data);
                    })
                    .catch((err) => {
                        this.clearSaving();
                        this.captureRequestErrors(err);
                    });
            } else {
                Service.create(data)
                    .then((res) => {
                        this.clearSaving();
                        this.setInfoMessage("Form saved successfully");
                        if (this.$route.path.includes("engineer/")) {
                            this.$router.push({
                                name: "engineer.forms.edit",
                                params: {
                                    id: res.data.id,
                                },
                            });
                        } else {
                            this.$router.push({
                                name: "office.forms.edit",
                                params: {
                                    id: res.data.id,
                                },
                            });
                        }
                    })
                    .catch((err) => {
                        this.clearSaving();
                        this.captureRequestErrors(err);
                    });
            }
        },
        deleteForm: function () {
            if (confirm("Are you sure you want to delete this form?")) {
                Service.delete(this.form).then(
                    (xhr) => {
                        this.setInfoMessage("Form successfully deleted");
                        this.$router.push({name: "office.forms.search"});
                    },
                    (error) => {
                        this.setErrorMessage("Could not delete this form");
                    }
                );
            }
        },

        removeActionPlanItem: function (index) {
            this.form.action_plan_items.splice(index, 1);
        },

        addActionPlanItem: function (requirement = "") {
            this.form.action_plan_items.push({
                ...initialActionPlanItem,
                requirement,
            });
        },

        autofillActionPlan: function () {
            if (
                this.form.action_plan_items.length > 0 &&
                this.form.action_plan_items[0].requirement !== ""
            ) {
                const confirm = window.confirm(
                    "Autofilling the action plan will delete any existing items, do you want to continue?"
                );
                if (!confirm) {
                    return;
                }
            }
            this.form.action_plan_items = [];
            this.addRiskToActionPlan(
                "prevent_fires",
                "Take reasonable measures to prevent fires of electrical origin"
            );
            this.addRiskToActionPlan(
                "fixed_install_tested",
                "Periodically inspect and test fixed installation"
            );
            this.addRiskToActionPlan(
                "portable_appliance_test",
                "Carry out portable appliance testing"
            );
            this.addRiskToActionPlan(
                "personal_equip_policy",
                "Adopt a policy regarding the use of personal electrical appliances"
            );
            this.addRiskToActionPlan(
                "trailing_leads_limit",
                "Adopt a limitation for trailing leads and adapters"
            );
            this.addRiskToActionPlan(
                "smoking_fires_prevent",
                "Take measures to prevent fires because of smoking"
            );
            this.addRiskToActionPlan(
                "smoking_prohibited_primises",
                "Prohibit smoking on the premises"
            );
            // Section 1 Risks
            this.addRiskToActionPlan(
                "smoking_prohibited_areas",
                "Prohibit smoking in appropriate areas"
            );
            this.addRiskToActionPlan(
                "smoking_arrangements",
                "Make suitable arrangements for those who wish to smoke"
            );
            this.addRiskToActionPlan(
                "arson_security",
                "Adopt basic security against arson"
            );
            this.addRiskToActionPlan(
                "fire_load",
                "Remove fire load available near to premises"
            );
            this.addRiskToActionPlan(
                "portable_heaters_avoidable",
                "Avoid using portable heaters where practical"
            );
            this.addRiskToActionPlan(
                "harzardous_appliances_avoided",
                "Remove the use of hazardous type applicances"
            );
            this.addRiskToActionPlan(
                "minimise_combustible_materials",
                "Take measures to minimize the hazard of ignition of combustible materials"
            );
            this.addRiskToActionPlan(
                "fixed_heating_maintenance",
                "Regularly maintain fixed heating installations"
            );
            this.addRiskToActionPlan(
                "prevent_cooking_fires",
                "Take measures to prevent fires from cooking"
            );
            this.addRiskToActionPlan(
                "filters_ductwork_cleaned",
                "Clean filters and ductwork regularly"
            );
            this.addRiskToActionPlan(
                "extinguishing_available",
                "Ensure suitable extinguishing appliances available"
            );
            this.addRiskToActionPlan(
                "lighting_protection",
                "Install a lighting protection system"
            );
            this.addRiskToActionPlan("housekeeping", "Improve housekeeping");
            this.addRiskToActionPlan(
                "combustible_materials_separated",
                "Separate combustible materials from ignition sources"
            );
            this.addRiskToActionPlan(
                "combustible_materials_accumilation",
                "Avoid unnecessary accumulation of combustible materials or waste"
            );
            this.addRiskToActionPlan(
                "hazardous_materials_storage",
                "Adopt appropriate storage of hazardous materials"
            );
            this.addRiskToActionPlan(
                "combustible_materials_storage",
                "Incorporate appropriate storage of combustible materials"
            );
            this.addRiskToActionPlan(
                "contractors_fire_safety",
                "Impose fire safety conditions on outside contractors"
            );
            this.addRiskToActionPlan(
                "contractor_work_permits",
                "Ensure satisfactory control over works carried out on the premises by outside contractors (including “hot work” permits)"
            );
            this.addRiskToActionPlan(
                "inhouse_work_permits",
                "Ensure suitable precautions taken in-house during “hot work”, including use of “hot work” permits"
            );
            this.addRiskToActionPlan(
                "fire_precautions_dangerous_substances",
                "Take adequate general fire precautions to address the hazards associated with dangerous substances used or stored within the premises"
            );
            this.addRiskToActionPlan(
                "risk_assessment_dsea_regs_2002",
                "Carry out a risk assessment as required by the Dangerous Substances and Explosive Atmospheres Regulations 2002"
            );
            this.addRiskToActionPlan(
                "other_hazards",
                "Address other significant fire hazards that warrant consideration including process hazards that impact on general fire precautions"
            );
            // TODO
            // Section 2 Risks
            this.addRiskToActionPlan("", "");
            this.addRiskToActionPlan("", "");
            this.addRiskToActionPlan("", "");
            this.addRiskToActionPlan("", "");
            this.addRiskToActionPlan("", "");
            this.addRiskToActionPlan("", "");
        },

        addRiskToActionPlan: function (risk, requirement) {
            if (this.form[risk] === "No") {
                this.addActionPlanItem(requirement);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.action-plan-table {
    .td-requirement,
    .td-response {
        width: 20%;
    }

    .td-target,
    .td-completed {
        width: 15%;
    }

    .td-buttons {
        width: 2%;
    }

    textarea {
        resize: vertical;
    }
}

.legend-table {
    border-spacing: 0;

    td {
        border: 1px solid black;
    }
}
</style>


// WEBPACK FOOTER //
// resources/assets/js/components/forms/FireRiskAssessment.vue